<template>
  <div class="app" :class="{ 'app--is-widget': $store.state.isWidget }">
    <version-checker
      :PFX="PROJ_PFX"
      :bundleVersion="appInfo.version"
    ></version-checker>
    <div class="app__preheader" ref="preheader">
      <preheader></preheader>
    </div>
    <LoaderBar></LoaderBar>
    <div
      class="app__main"
      :class="{
        'nav-open': $sidebar.showSidebar,
        'app__main--disableSelect': $store.state.appInterface.disableSelect,
      }"
    >
      <transition name="fade" mode="out-in">
        <router-view :class="{ blur: visibleReloginOnFly }"></router-view>
      </transition>
      <transition name="fade" mode="out-in">
        <relogin-on-fly
          style="animation-duration: 0.15s"
          v-if="visibleReloginOnFly"
          @login-canceled="onReloginCancel()"
          @login-success="onReloginSuccess()"
        ></relogin-on-fly>
      </transition>
    </div>
    <v-style
      v-for="(style, uid) in $store.state.appInterface.styles"
      :key="uid"
    >
      {{ style.query }} {
      <template v-for="(value, param) in style.params"
        >{{ param }}: {{ value }};
      </template>
      }
    </v-style>
  </div>
</template>

<script>
import ReloginOnFly from "./components/ReloginOnFly/ReloginOnFly.vue";
import auth from "src/services/auth";
import localStorageTransport from "./services/localStorageTransport";
import Preheader from "./components/Preheader/Preheader";
import LoaderBar from "./components/LoaderBar/LoaderBar";

import versionChecker from "@/plugins/VersionChecker/VersionChecker.vue";
import {
  PROJ_PFX,
  CONF_BASE_URL,
  LANG,
  WIDGET_REQUIRED_MESSENGER,
  PAGE_TITLE,
  PAGE_DESCRIPTION,
} from "@/constants";
import packagejson from "@/lib/packagejson";
import config from "src/config";
import axios from "axios";
import {getCheckedLang} from "@/services/langStorage";
import _ from "lodash";

const changeLangTimeout = 1000;

export default {
  name: "App",
  components: {
    Preheader,
    ReloginOnFly,
    LoaderBar,
    versionChecker,
  },
  watch: {
    $route() {
      setTimeout(() => {
        this.$root.$emit("resetScroll");
        this.$root.$emit("focusOnContent");
      }, 150);
    },
    hasActivatedMessenger(value) {},
    "$store.state.user.user.data.msgr.mgs": {
      handler(value, oldValue) {
        if (
          oldValue === undefined ||
          value === undefined ||
          value.length !== oldValue.length
        ) {
          setTimeout(() => {
            if (
              this.isWidget &&
              !this.hasActivatedMessenger &&
              this.isWidgetRequiredMessenger
            ) {
              if (this.isWidgetRequiredMessenger === "soft") {
                window.parent.postMessage(
                  `lsn_widget_required_message_soft`,
                  "*"
                );
              }

              if (this.isWidgetRequiredMessenger === "hard") {
                window.parent.postMessage(
                  `lsn_widget_required_message_hard`,
                  "*"
                );
              }
            }

            if (
              this.isWidget &&
              this.hasActivatedMessenger &&
              this.isWidgetRequiredMessenger
            ) {
              if (this.isWidgetRequiredMessenger === "soft") {
                window.parent.postMessage(
                  `lsn_widget_required_message_soft_no`,
                  "*"
                );
              }
              if (this.isWidgetRequiredMessenger === "hard") {
                window.parent.postMessage(
                  `lsn_widget_required_message_hard_no`,
                  "*"
                );
              }
            }
          }, 100);
        }
      },
      deep: true,
    },
  },
  data() {
    return {
      PROJ_PFX,
      visibleReloginOnFly: false,
      appInfo: packagejson.getKeys(),
      WIDGET_REQUIRED_MESSENGER,
    };
  },
  onIdle() {
    this.relogin();
  },
  onActive() {},
  computed: {
    seoPageTitle() {
      const configPageTitle = config(PAGE_TITLE)

      if (configPageTitle) {
        return configPageTitle;
      }

      return 'Student Notification System';
    },
    seoPageDescription() {
      const configPageDescription = config(PAGE_DESCRIPTION)

      if (configPageDescription) {
        return configPageDescription;
      }

      return 'Ultimate All-in-One Hub for Effective Mass Notification in Universities'
    },
    hasActivatedMessenger() {
      if (
        this.$store.state.user.user &&
        this.$store.state.user.user.data &&
        this.$store.state.user.user.data.msgr &&
        this.$store.state.user.user.data.msgr.mgs &&
        this.$store.state.user.user.data.msgr.mgs.length
      ) {
        return true;
      }

      return false;
    },
    isWidget() {
      return this.$store.state.isWidget;
    },
    isWidgetRequiredMessenger() {
      return config(WIDGET_REQUIRED_MESSENGER);
    },
  },
  metaInfo() {
    return {
      title: this.seoPageTitle,
      meta: [
        {
          name: 'description',
          content: this.seoPageDescription
        }
      ]
    }
  },
  methods: {
    loginUser(accessToken, refreshToken) {
      let vm = this;

      localStorage.setItem("refresh_token", refreshToken);
      auth.setToken(accessToken);

      vm.$store
        .dispatch("user/loadUser", true)
        .then(function (response) {
          if (
            response &&
            response.data &&
            response.data.msgr &&
            response.data.msgr.mgs &&
            response.data.msgr.mgs.length
          ) {
            window.location.href = "/#/messeger/hist";
          } else {
            window.location.href = "/#/settings/messengers";
          }
        })
        .catch(() => {
          vm.$emit("getError", vm.$t("login.check_login_and_password"));
          document.getElementById("loader1").classList.add("isHide");
        });
    },
    onResize: _.debounce(function () {
      this.defineCSSVars();
    }, 200),
    defineCSSVars() {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    },
    checkAccessTokenTime() {
      setInterval(() => {
        if (!auth.isAuthenticated()) {
          return;
        }
        const accessTokenUntilStorage =
          +localStorage.getItem("token_live_until");
        const accessTokenUntilTime = new Date(
          accessTokenUntilStorage
        ).getTime();
        const nowTime = new Date().getTime();

        if (accessTokenUntilTime <= nowTime) {
          const url = config(CONF_BASE_URL) + "/openid/refresh";
          const refreshToken = localStorage.getItem("refresh_token");

          axios.post(url, { refresh_token: refreshToken }).then((response) => {
            const accessToken = response.data.access_token;
            const refreshTokenNew = response.data.refresh_token;

            auth.setToken(accessToken);
          });
        }
      }, 180000);
    },
    onReloginCancel() {
      this.visibleReloginOnFly = false;
    },
    onReloginSuccess() {
      this.visibleReloginOnFly = false;
      this.$root.$emit("on-login");
    },
    relogin() {
      /* if (this.$route.path !== '/login') {
          this.visibleReloginOnFly = true
          auth.logout();
        } */
    },
  },
  mounted() {
    if (window.self !== window.top) {
      this.$store.commit("setWidget", true);

      setTimeout(() => {
        const buttonsTexts = `${this.$t("common.widget.close")} : ${this.$t(
          "common.widget.open_in_new_tab"
        )}`;

        window.parent.postMessage(`lsn_loaded`, "*");

        if (!auth.readToken()) {
          window.parent.postMessage(`lsn_loaded_without_user`, "*");
        }

        window.parent.postMessage(`lang_${buttonsTexts}`, "*");
      }, 100);
    }

    let vm = this;

    window.addEventListener("message", function (event) {
      if (typeof event.data === "string" && event.data.startsWith("lsn_")) {
        const messageText = event.data.substring(4);
        // alert('получил текст в iframe')
        // const replyMessage = 'lsn_Привет, родительская страница!';
        // event.source.postMessage(replyMessage, event.origin);
        // alert('отправил текст из iframe')
        if (messageText === "logout_user") {
          auth.logout();
        }

        // Parent can notify the iframe it became visible again (e.g. after the
        // close/open toggle). Needed for parents that hide via
        // `visibility:hidden`, where the iframe cannot self-detect re-show.
        // Embed snippet: iframe.contentWindow.postMessage('lsn_widget_shown','*')
        if (messageText === "widget_shown") {
          vm.$root.$emit("lsn-widget-shown");
        }

        if (messageText.startsWith("login_user")) {
          const loginData = messageText.split(":");

          auth.logout();
          vm.loginUser(loginData[1], loginData[2]);
        }
      }
    });

    window.addEventListener('resize', this.onResize);
    this.defineCSSVars();

    this.$nextTick(() => {
      const lang = getCheckedLang() || config(LANG) || "en";

      this.$store.commit("setLang", lang);
    });

    this.checkAccessTokenTime();

    let urlSearchParams = new URLSearchParams(window.location.search);
    let urlParams = Object.fromEntries(urlSearchParams.entries());
    let code = urlParams.code;

    if (code) {
      document.getElementById("loader1").classList.remove("isHide");
      auth.logout();

      const client_id = "lsnuser";
      const client_secret = "bbRW6GZ68tNe";
      const responseState = "test";
      const redirect_uri = config(CONF_BASE_URL) + "/openid/authorize";
      const params = {
        code,
        client_id,
        client_secret,
        redirect_uri,
        responseState,
      };

      axios
        .post(config(CONF_BASE_URL) + "/openid/token", params)
        .then(function (response) {
          const access_token = response.data.access_token;
          const expire = response.data.exp_ms;
          const refresh_token = response.data.refresh_token;

          vm.loginUser(access_token, refresh_token);

          vm.visibleReloginOnFly = false;
        })
        .catch(function (error) {
          console.error("token error", error);
          document.getElementById("loader1").classList.add("isHide");
        });
    }

    console.log("name:", this.appInfo.name);
    console.log("version:", this.appInfo.version);

    localStorageTransport.on("login", async () => {
      console.log("[login from another tab]");
      await this.$store.dispatch("user/loadUser", true);
      this.visibleReloginOnFly = false;
      if (this.$route.name === "login") {
        this.$router.push("/");
      }
    });
    localStorageTransport.on("logout", () => {
      console.log("[logout from another tab]");
      this.$router.push("/login");
    });

    this.$bus.$on("relogin-on-fly:request", () => {
      this.visibleReloginOnFly = true;
    });

    this.$bus.$on("close-relogin-on-fly", () => {
      this.visibleReloginOnFly = false;
    });

    this.$bus.$on("E_SESS_INACTIVE", () => {
      console.log("user not logined");
      this.$router.push("/login");
      auth.logout();
    });
    this.$bus.$on("errorUserMessage", (userMessage) => {
      this.$notify({
        title: this.$t("labels.error"),
        message: userMessage,
        type: "error",
        offset: 75,
      });
    });
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
  }
};
</script>
<style lang="scss">
$animationDuration: 0.24s;
@import "~vue2-animate/src/sass/vue2-animate.scss";

@import "./assets/fonts/Roboto/stylesheet";

@import "./assets/styles/scss/typography";
// library imports
@import "./assets/sass/paper-dashboard.scss";
@import "./assets/sass/element_variables.scss";
@import "./assets/sass/demo.scss";
@import "./assets/sass/print.scss";

a {
    outline: none;
}

html,
body {
  height: 100%;
  overflow: hidden;
}

.app {
  display: flex;
  flex-flow: column;
  height: 100%;
  overflow: hidden;

  &__main {
    flex: 1;
    display: flex;
    overflow: auto;

    &--disableSelect {
      * {
        user-select: none;
        -webkit-user-select: none;
      }
    }
  }
}

.notifications.vue-notifyjs {
  .notification-list-move {
    transition: transform 0.3s, opacity 0.4s;
  }

  .notification-list-item {
    display: inline-block;
    margin-right: 10px;
  }

  .notification-list-enter-active,
  .notification-list-leave-active {
    transition: opacity 0.4s;
  }

  .notification-list-enter, .notification-list-leave-to /* .list-leave-active for <2.1.8 */ {
    opacity: 0;
  }
}

.el-tabs__nav-scroll {
  overflow: auto;
}

.blur {
  -webkit-filter: blur(8px);
  -moz-filter: blur(8px);
  filter: blur(8px);
  filter: progid:DXImageTransform.Microsoft.Blur(pixelRadius=8);
  overflow: hidden;
  transform: translateZ(0);
}
</style>